import { Link } from 'react-router-dom';
import { ArrowRight, Newspaper } from 'lucide-react';

interface NewsItem {
  id: string;
  headline: string;
  summary: string;
  date: string;
}

interface NewsCardProps {
  latestNews?: NewsItem;
  previousNews: NewsItem[];
  totalCount?: number;
  loading?: boolean;
}

const shortDate = (date: string) =>
  new Date(date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });

/** "Ny" only for news from the last two weeks */
const isRecent = (date: string) => Date.now() - new Date(date).getTime() < 14 * 24 * 60 * 60 * 1000;

/**
 * Dashboard news: the latest item in full (summary), earlier headlines below, and a footer link that
 * sits at the bottom of the card so it lines up with the card beside it.
 */
export default function NewsCard({ latestNews, previousNews, totalCount, loading }: NewsCardProps) {
  return (
    <section aria-labelledby="news-card-title" className="card flex flex-col">
      <div className="flex items-center gap-2 px-5 pt-5">
        <Newspaper className="h-4 w-4 text-gray-500" aria-hidden />
        <h3 id="news-card-title" className="text-[15px] font-semibold text-ink">
          Nyheter
        </h3>
      </div>

      {loading ? (
        <p className="px-5 py-4 text-sm text-gray-500">Hämtar nyheter…</p>
      ) : !latestNews ? (
        <p className="px-5 py-4 text-sm text-gray-500">Inga nyheter just nu. När din åkericentral publicerar något syns det här.</p>
      ) : (
        <>
          {/* Latest, with a brand line on the left */}
          <Link to="/news" className="group mx-5 mt-4 block border-l-2 border-[#16acce] pl-4 text-left transition-colors hover:border-[#d3a73b]">
            <span className="flex items-center gap-2 text-xs text-gray-500">
              <time dateTime={latestNews.date}>{shortDate(latestNews.date)}</time>
              {isRecent(latestNews.date) && (
                <span className="rounded-full bg-[#fbf4e2] px-2 py-px text-[11px] font-medium text-brass-ink">Ny</span>
              )}
            </span>
            <span className="mt-1 block text-pretty text-[16px] font-semibold leading-snug text-ink transition-colors group-hover:text-blue-700">
              {latestNews.headline}
            </span>
            {latestNews.summary && (
              <span className="mt-1.5 line-clamp-4 block text-sm leading-relaxed text-gray-600">{latestNews.summary}</span>
            )}
          </Link>

          {/* Earlier headlines */}
          {previousNews.length > 0 && (
            <div className="mt-5 px-5">
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500">Tidigare</p>
              <ul className="mt-1 divide-y divide-gray-100">
                {previousNews.map((item) => (
                  <li key={item.id}>
                    <Link to="/news" className="group flex items-baseline justify-between gap-4 py-2.5 text-left">
                      <span className="line-clamp-1 text-sm font-medium text-gray-800 transition-colors group-hover:text-blue-700">
                        {item.headline}
                      </span>
                      <time dateTime={item.date} className="shrink-0 whitespace-nowrap text-xs tabular-nums text-gray-500">
                        {shortDate(item.date)}
                      </time>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Footer at the bottom of the card */}
      <div className="mt-auto border-t border-gray-100 px-5 py-3">
        <Link to="/news" className="group inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 transition-colors hover:text-blue-800">
          {totalCount && totalCount > 1 ? `Alla ${totalCount} nyheter` : 'Till nyheter'}
          <ArrowRight className="h-4 w-4 transition-transform duration-300 ease-fluid group-hover:translate-x-0.5" aria-hidden />
        </Link>
      </div>
    </section>
  );
}
